<script lang="ts" setup>
import { ref } from 'vue';
import type { ValidationOptions } from '@idem/tipos/validation-options.type';
import type { ValidationResult } from '@idem/tipos/validation-result.type';

import ErrorCaption from './error-caption.component.vue';
import CampoValidante from './campo-validante.vue';

const errorMessage = ref('');
const props = defineProps<{
  label?: string | undefined;
  field: string;
  value?: string | undefined;
  validator?: (validationOptions: ValidationOptions) => ValidationResult;
  disabled?: boolean;
  placeholder?: string;
}>();
const emit = defineEmits(['update', 'error', 'blur']);

function handleUpdate(e: Event) {
  const inputValue = (e.target as HTMLInputElement).value;
  errorMessage.value = '';
  emit('update', { [props.field]: inputValue });
}

function handleError(code: string, result: ValidationResult) {
  emit('error', `${props.field}:${code}`);
  if (result.message) {
    errorMessage.value = result.message(props.field);
  }
}
</script>

<template>
  <div class="mx-auto w-full max-w-md text-center">
    <label
      v-if="props.label"
      class="text-[18px] font-bold text-[#1A1A2E]"
      :data-testid="props.field + '-label'"
      :for="props.field"
      >{{ props.label }}</label
    >
    <div
      id="input-container"
      class="mx-auto mt-4 flex max-w-[340px] items-center justify-center gap-2 rounded-xl px-6 py-4 shadow-[0_0_10px_rgba(0,0,0,0.05)]"
      :class="props.disabled ? 'pointer-events-none cursor-not-allowed bg-[#D9D9D9]' : 'bg-white'"
    >
      <campo-validante :validator="props.validator" @error="handleError">
        <template v-slot="slotProps">
          <input
            :id="props.field"
            :data-testid="props.field + '-input'"
            :value="props.value"
            :disabled="props.disabled === true"
            class="h-3.5 w-full bg-transparent text-center text-[14px] placeholder-gray-400 outline-none"
            @input="e => slotProps.validar(handleUpdate)(e)"
            @blur="emit('blur')"
            :placeholder="props.placeholder"
          />
        </template>
      </campo-validante>
    </div>
    <error-caption
      class="color-error"
      :data-testid="`${props.field}:error`"
      :error="errorMessage"
    />
  </div>
</template>

<style scoped>
#input-container {
  border-bottom: 1px solid var(--idem-gris);
}
input:disabled {
  cursor: not-allowed;
}
</style>
